import { useEffect, useState } from "react"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { SignaturePad } from "@/components/SignaturePad"
import { QrSignatureControllerNotice, QrSignaturePrivacyNotice } from "@/components/attendance/QrSignaturePrivacyNotice"
import {
  matchPublicAttendance,
  signPublicAttendance,
  PublicAttendanceError,
  publicAttendanceTerminalStep,
  type AttendanceMatch,
  type PublicAttendanceOrganization,
} from "@/services/publicAttendance"
import { AlertTriangle, ArrowLeft, CheckCircle2, ClipboardList } from "lucide-react"
import { LoadErrorAlert } from "@/components/LoadErrorAlert"
import { portalSignErrorMessage } from "@/lib/portal-attendance"
import { translateError } from "@/lib/error-messages"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"

// Flux kiosque mobile-first (~25 s par stagiaire) :
//   identify → sign → signed, avec écrans dédiés "expired" (token révoqué)
//   et "already-signed" (409). Exigence RGPD : le roster complet n'est JAMAIS
//   affiché — le stagiaire tape au moins 3 lettres de son nom et le serveur
//   renvoie au plus 5 correspondances.
type Step = "identify" | "sign" | "signed" | "already-signed" | "expired" | "locked" | "wrong-day" | "closed"

/** Écran définitif d'un refus de l'API publique. */
type TerminalStep = "locked" | "wrong-day" | "expired" | "closed"

type SignMode = "draw" | "type"

export default function PublicAttendanceSignPage() {
  const { token } = useParams<{ token: string }>()

  const [step, setStep] = useState<Step>("identify")
  const [query, setQuery] = useState("")
  // null = aucune recherche effectuée (ne pas afficher "Aucun nom trouvé")
  const [matches, setMatches] = useState<AttendanceMatch[] | null>(null)
  const [selected, setSelected] = useState<AttendanceMatch | null>(null)
  const [mode, setMode] = useState<SignMode>("draw")
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [typedName, setTypedName] = useState("")
  const [rulesAccepted, setRulesAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [signedName, setSignedName] = useState("")
  // Remonter le SignaturePad (clé) pour effacer le canvas à chaque reset
  const [padKey, setPadKey] = useState(0)
  // Refus « pas le jour de la séance » : le message porte la date de la feuille.
  const [wrongDayMessage, setWrongDayMessage] = useState("")
  // Recherche en échec (panne, réseau) : un état d'erreur avec « Réessayer »,
  // jamais « Aucun nom trouvé » qui ferait croire à une absence d'inscription.
  const [searchFailed, setSearchFailed] = useState(false)
  const [searchAttempt, setSearchAttempt] = useState(0)
  // Organisme responsable, nommé en pied de page dès la sonde.
  const [organization, setOrganization] = useState<O.Option<PublicAttendanceOrganization>>(O.none)

  // Écran définitif d'un refus (lien expiré, feuille finalisée, autre jour).
  const showTerminal = (terminal: TerminalStep, err: unknown) => {
    if (terminal === "wrong-day" && err instanceof PublicAttendanceError) {
      setWrongDayMessage(translateError({ response: { status: err.status, data: err.body } }))
    }
    setStep(terminal)
  }

  // Sonde de validité au chargement : un token révoqué/expiré renvoie 404 sur
  // /match, une feuille finalisée 422 ATTENDANCE_RECORDS_LOCKED — l'écran
  // définitif s'affiche sans attendre la 1re recherche.
  // La recherche porte sur 3 lettres arbitraires. Seul l'organisme sert.
  useEffect(() => {
    if (!token) return
    matchPublicAttendance(token, "zzz")
      .then((res) => setOrganization(res.organization))
      .catch((err: unknown) => {
        O.map((terminal: TerminalStep) => showTerminal(terminal, err))(publicAttendanceTerminalStep(err))
      })
  }, [token])

  // Recherche debouncée (300 ms) dès 3 caractères saisis. Le vidage des
  // résultats sous 3 caractères est fait dans le onChange de l'input.
  useEffect(() => {
    if (!token) return
    const q = query.trim()
    if (q.length < 3) return
    const handle = setTimeout(() => {
      matchPublicAttendance(token, q)
        .then((res) => {
          setSearchFailed(false)
          setMatches(res.matches)
        })
        .catch((err: unknown) => {
          const terminal = publicAttendanceTerminalStep(err)
          if (O.isSome(terminal)) {
            showTerminal(terminal.value, err)
            return
          }
          setMatches(null)
          setSearchFailed(true)
        })
    }, 300)
    return () => clearTimeout(handle)
  }, [query, token, searchAttempt])

  const resetToIdentify = () => {
    setStep("identify")
    setQuery("")
    setMatches(null)
    setSelected(null)
    setMode("draw")
    setSignatureDataUrl(null)
    setTypedName("")
    setRulesAccepted(false)
    setSubmitting(false)
    setSubmitError(null)
    setSignedName("")
    setPadKey((k) => k + 1)
  }

  const handleModeChange = (value: string) => {
    setMode(value as SignMode)
    // Radix démonte le contenu de l'onglet inactif : le canvas est vidé au
    // retour sur "Signer au doigt" — on purge le tracé mémorisé pour que le
    // bouton Valider reflète l'état visible.
    setSignatureDataUrl(null)
    setPadKey((k) => k + 1)
  }

  const hasSignature = mode === "draw" ? signatureDataUrl !== null : typedName.trim().length > 0
  const canSubmit = hasSignature && rulesAccepted && !submitting

  const handleSubmit = () => {
    if (!token || !selected || !canSubmit) return
    setSubmitting(true)
    setSubmitError(null)
    const payload =
      mode === "draw"
        ? {
            registration_id: selected.registration_id,
            signature_data: signatureDataUrl as string,
            internal_rules_acknowledged: true,
          }
        : {
            registration_id: selected.registration_id,
            typed_name: typedName.trim(),
            internal_rules_acknowledged: true,
          }
    signPublicAttendance(token, payload)
      .then((res) => {
        setSignedName(res.display_name)
        setStep("signed")
      })
      .catch((err: unknown) => {
        setSubmitting(false)
        if (err instanceof PublicAttendanceError && err.status === 409) {
          setStep("already-signed")
          return
        }
        // Feuille finalisée entre l'affichage du QR et la signature, ou lien
        // expiré : refus définitif, jamais « réessayez ».
        const terminal = publicAttendanceTerminalStep(err)
        if (O.isSome(terminal)) {
          showTerminal(terminal.value, err)
          return
        }
        // Autre refus 4xx (jour de la séance dépassé…) : message du refus,
        // sans « réessayez » ; panne ou réseau : invitation à réessayer.
        if (err instanceof PublicAttendanceError) {
          setSubmitError(portalSignErrorMessage({ response: { status: err.status, data: err.body } }))
          return
        }
        setSubmitError("Impossible d'enregistrer votre signature. Vérifiez votre connexion et réessayez.")
      })
  }

  if (!token) {
    return (
      <PublicShell organization={organization}>
        <ExpiredScreen />
      </PublicShell>
    )
  }

  if (step === "expired") {
    return (
      <PublicShell organization={organization}>
        <ExpiredScreen />
      </PublicShell>
    )
  }

  if (step === "locked") {
    return (
      <PublicShell organization={organization}>
        <Card data-test="qr-locked">
          <CardContent className="space-y-4 py-10 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
            <h2 className="text-xl font-semibold">La feuille d'émargement est finalisée.</h2>
            <p className="text-sm text-muted-foreground">
              Elle n'accepte plus de signature : rapprochez-vous de votre formateur.
            </p>
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  if (step === "closed") {
    return (
      <PublicShell organization={organization}>
        <TenantClosedNotice />
      </PublicShell>
    )
  }

  if (step === "wrong-day") {
    return (
      <PublicShell organization={organization}>
        <Card data-test="qr-wrong-day">
          <CardContent className="space-y-4 py-10 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
            <h2 className="text-xl font-semibold">Ce n'est pas le jour de cette séance.</h2>
            <p className="text-sm text-muted-foreground">{wrongDayMessage}</p>
            <p className="text-sm text-muted-foreground">Rapprochez-vous de votre formateur.</p>
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  if (step === "signed") {
    return (
      <PublicShell organization={organization}>
        <Card data-test="qr-signed">
          <CardContent className="space-y-4 py-10 text-center">
            <div className="flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-green-700">Présence signée — merci {signedName}</h2>
            <p className="text-sm text-muted-foreground">Vous pouvez rendre l'appareil au formateur.</p>
            <Button className="h-12 w-full" onClick={resetToIdentify} data-test="qr-sign-another">
              Signer pour une autre personne
            </Button>
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  if (step === "already-signed") {
    return (
      <PublicShell organization={organization}>
        <Card data-test="qr-already-signed">
          <CardContent className="space-y-4 py-10 text-center">
            <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
            <h2 className="text-xl font-semibold">Cette présence est déjà signée.</h2>
            <p className="text-sm text-muted-foreground">
              Si ce n'est pas vous qui avez signé, rapprochez-vous de votre formateur.
            </p>
            <Button variant="outline" className="h-12 w-full" onClick={resetToIdentify} data-test="qr-back-to-identify">
              Revenir à l'identification
            </Button>
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  if (step === "sign" && selected) {
    return (
      <PublicShell organization={organization}>
        <Card data-test="qr-sign-step">
          <CardHeader>
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2"
              onClick={() => {
                setSelected(null)
                setSignatureDataUrl(null)
                setTypedName("")
                setRulesAccepted(false)
                setSubmitError(null)
                setPadKey((k) => k + 1)
                setStep("identify")
              }}
              data-test="qr-back"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Retour
            </Button>
            <CardTitle data-test="qr-selected-name">{selected.display_name}</CardTitle>
            <CardDescription>Signez pour attester votre présence</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={mode} onValueChange={handleModeChange}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="draw" data-test="qr-mode-draw">
                  Signer au doigt
                </TabsTrigger>
                <TabsTrigger value="type" data-test="qr-mode-type">
                  Taper mon nom
                </TabsTrigger>
              </TabsList>
              <TabsContent value="draw">
                <div data-test="qr-signature-pad">
                  <SignaturePad key={padKey} width={400} height={180} onSignatureChange={setSignatureDataUrl} />
                </div>
              </TabsContent>
              <TabsContent value="type" className="space-y-2">
                <Label htmlFor="qr-typed-name">Votre prénom et nom</Label>
                <Input
                  id="qr-typed-name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Prénom Nom"
                  autoComplete="name"
                  className="h-12"
                  data-test="typed-name-input"
                />
                <p className="text-sm text-muted-foreground">Votre nom tapé vaut signature.</p>
              </TabsContent>
            </Tabs>

            <div className="flex items-start space-x-3 rounded-md border p-3">
              <Checkbox
                id="qr-rules"
                checked={rulesAccepted}
                onCheckedChange={(checked) => setRulesAccepted(checked === true)}
                data-test="qr-rules-checkbox"
              />
              <label htmlFor="qr-rules" className="cursor-pointer text-sm leading-5">
                J'atteste avoir pris connaissance du règlement intérieur
              </label>
            </div>

            {submitError && (
              <p className="text-sm text-destructive" role="alert" data-test="qr-sign-error">
                {submitError}
              </p>
            )}

            <Button
              className="h-12 w-full"
              disabled={!canSubmit}
              onClick={handleSubmit}
              aria-describedby="qr-submit-help"
              data-test="qr-submit"
            >
              {submitting ? "Enregistrement..." : "Valider ma présence"}
            </Button>
            <p id="qr-submit-help" className="text-xs text-muted-foreground">
              Pour valider : signez au doigt ou tapez votre nom, puis cochez l'attestation du règlement intérieur.
            </p>
            <QrSignaturePrivacyNotice />
          </CardContent>
        </Card>
      </PublicShell>
    )
  }

  return (
    <PublicShell organization={organization}>
      <Card data-test="qr-identify-step">
        <CardHeader>
          <CardTitle>Émargement — signez votre présence</CardTitle>
          <CardDescription>Identifiez-vous pour signer la feuille de présence de la séance.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="qr-name">Les 3 premières lettres de votre nom</Label>
            <Input
              id="qr-name"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setSearchFailed(false)
                if (e.target.value.trim().length < 3) setMatches(null)
              }}
              placeholder="Ex : DUP"
              autoFocus
              autoComplete="off"
              className="h-12 text-lg"
              data-test="qr-name-input"
            />
          </div>

          {searchFailed && (
            <LoadErrorAlert
              message="La recherche n'a pas abouti. Vérifiez votre connexion et réessayez."
              onRetry={() => {
                setSearchFailed(false)
                setSearchAttempt((attempt) => attempt + 1)
              }}
              isRetrying={false}
              dataTest="qr-search-error"
            />
          )}

          {matches !== null && matches.length === 0 && (
            <p className="text-sm text-muted-foreground" data-test="qr-no-match">
              Aucun nom trouvé — précisez une lettre de plus ou rapprochez-vous de votre formateur.
            </p>
          )}

          {matches !== null && matches.length > 0 && (
            <div className="space-y-2">
              {matches.map((match) => (
                <Button
                  key={match.registration_id}
                  variant="outline"
                  className="h-12 w-full justify-start text-base"
                  onClick={() => {
                    setSelected(match)
                    setStep("sign")
                  }}
                  data-test="qr-match-option"
                >
                  {match.display_name}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </PublicShell>
  )
}

/**
 * Jeton inconnu, échu ou retiré.
 * Le code projeté se renouvelle seul : le stagiaire le rescanne.
 * Le formateur n'est qu'un recours.
 *
 * @example
 * render(<ExpiredScreen />) // « Ce code n'est plus valable : scannez de nouveau… »
 */
export function ExpiredScreen() {
  return (
    <Card data-test="qr-expired">
      <CardContent className="space-y-4 py-10 text-center">
        <AlertTriangle className="mx-auto h-10 w-10 text-orange-500" />
        <h2 className="text-xl font-semibold">Ce code n'est plus valable : scannez de nouveau le code projeté en salle.</h2>
        <p className="text-sm text-muted-foreground" data-test="qr-expired-help">
          Si le code projeté ne fonctionne pas, rapprochez-vous de votre formateur.
        </p>
      </CardContent>
    </Card>
  )
}

function PublicShell({
  children,
  organization,
}: {
  children: React.ReactNode
  organization: O.Option<PublicAttendanceOrganization>
}) {
  // L'organisme est nommé dès qu'il est connu.
  // Un écran de refus ne recueille rien.
  const footer = pipe(
    organization,
    O.map((known) => (
      <footer className="mx-auto w-full max-w-md p-4">
        <QrSignatureControllerNotice organization={known} />
      </footer>
    )),
    O.toNullable,
  )
  return (
    <div className="flex min-h-screen flex-col bg-muted/30">
      <header className="border-b bg-white">
        <div className="mx-auto flex max-w-md items-center gap-2 p-4 text-base font-medium">
          <ClipboardList className="h-5 w-5 text-primary" />
          Émargement
        </div>
      </header>
      <main className="mx-auto w-full max-w-md flex-1 space-y-6 p-4">{children}</main>
      {footer}
    </div>
  )
}
